// screen: Registered viewers — activity log (change viewer-crm)
// Registered viewers — activity log (change viewer-crm, design §4.5): account events (sign-up, sign-in, consent
// changes, the viewer's own export / deletion) and staff actions (personal data viewed, exports, blocks, deletions),
// newest first. viewers:pii. Kept 2 years.
import * as React from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useLingui } from '@lingui/react/macro';
import { Button, Select, Skeleton } from '@viewstream/ui';
import { fmtDateTime } from '@viewstream/i18n';
import { appTitle } from '~/app/brand';
import { useTenant } from '~/app/tenant';
import { useLocale } from '~/app/locale';
import { can } from '~/app/permissions';
import { useViewerAudit } from '~/app/viewers';
import { actorText, useAuditActionLabel, ViewersHeader, VIEWERS_PII } from '~/app/ViewersNav';

export const Route = createFileRoute('/_layout/t/$slug/viewers/audit')({ component: Screen });

const ACTIONS = ['pii_viewed', 'exported_csv', 'package_exported', 'blocked', 'unblocked', 'signed_out_by_staff', 'reset_sent', 'anonymised', 'deleted_by_staff',
  'request_opened', 'request_closed', 'signed_up', 'signed_in', 'consent_changed', 'exported', 'deleted', 'purged'];

function Screen() {
  const { t } = useLingui();
  const { tenant, scopes } = useTenant();
  const { locale, tz } = useLocale();
  React.useEffect(() => { document.title = appTitle(t`Activity log`); }, [t]);
  const [action, setAction] = React.useState('');
  const [cursor, setCursor] = React.useState<string | undefined>();
  const allowed = can(scopes, VIEWERS_PII);
  const audit = useViewerAudit(tenant.slug, { action: action || undefined, cursor }, allowed);
  const label = useAuditActionLabel();
  if (!allowed) return <p className="p-6 text-sm">{t`The activity log is shown to Admins and Owners.`}</p>;
  return (
    <div>
      <ViewersHeader tab="audit" />
      <p className="mb-3 max-w-3xl text-sm text-fg-muted">{t`Who looked at a viewer's personal data, who exported, blocked or deleted, and what viewers did in their own accounts. Kept for 2 years.`}</p>
      <div className="mb-3 w-64">
        <Select ariaLabel={t`Action`} value={action} onValueChange={(v) => { setCursor(undefined); setAction(v); }}
          options={[{ value: '', label: t`All actions` }, ...ACTIONS.map((a) => ({ value: a, label: label(a) }))]} />
      </div>
      {audit.isPending ? <Skeleton className="h-32 w-full" /> : (
        <>
          <ul className="divide-y divide-border rounded-lg border border-border text-sm" data-testid="viewer-audit-log">
            {(audit.data?.items ?? []).map((e, i) => (
              <li key={i} className="flex flex-wrap items-center justify-between gap-2 p-2">
                <span>{fmtDateTime(e.at, { locale, tz })} · {label(e.action)}</span>
                <span className="text-fg-muted">
                  {actorText(e.actor, audit.data?.actors, t`The viewer`)}
                  {e.account_id ? <> · <Link to={'/t/$slug/viewers/$viewerId' as never} params={{ slug: tenant.slug, viewerId: e.account_id } as never} className="text-primary hover:underline">{t`viewer`}</Link></> : null}
                </span>
              </li>
            ))}
          </ul>
          {(audit.data?.items ?? []).length === 0 ? <p className="mt-2 text-sm text-fg-muted">{t`Nothing recorded yet.`}</p> : null}
          <div className="mt-3 flex gap-2">
            {cursor ? <Button variant="ghost" onClick={() => setCursor(undefined)}>{t`Newest`}</Button> : null}
            {audit.data?.next_cursor ? <Button variant="secondary" onClick={() => setCursor(audit.data!.next_cursor!)}>{t`Older`}</Button> : null}
          </div>
        </>
      )}
    </div>
  );
}
